/**
 * Refer Rank — hoist Yotpo tile, masked reward list, rules dialog.
 */
class ReferRank extends HTMLElement {
  static YOTPO_TIMEOUT_MS = 12000;

  connectedCallback() {
    if (this.dataset.referRankReady === 'true') return;
    this.dataset.referRankReady = 'true';

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.yotpoMoved = false;
    this.yotpoObserver = null;
    this.yotpoSlotObserver = null;
    this.yotpoTimer = 0;
    this.intersectionObserver = null;

    this.rulesDialog = this.querySelector('[data-refer-rank-rules-dialog]');
    this.yotpoHost = this.querySelector('[data-refer-rank-yotpo-host]');
    this.yotpoSlot = this.querySelector('[data-refer-rank-yotpo-slot]');
    this.yotpoFallback = this.querySelector('[data-refer-rank-yotpo-fallback]');

    this.addEventListener('click', this.onClick, { signal });

    if (this.rulesDialog) {
      this.rulesDialog.addEventListener('click', this.onRulesDialogClick, { signal });
      this.rulesDialog.addEventListener('close', this.onRulesDialogClose, { signal });
    }

    this.setupList();
    this.setupYotpo();
    this.setupIntersectionPause();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;

    if (this.yotpoObserver) {
      this.yotpoObserver.disconnect();
      this.yotpoObserver = null;
    }
    if (this.yotpoSlotObserver) {
      this.yotpoSlotObserver.disconnect();
      this.yotpoSlotObserver = null;
    }
    if (this.yotpoTimer) {
      window.clearTimeout(this.yotpoTimer);
      this.yotpoTimer = 0;
    }
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }

    this.setRulesScrollLock(false);
    this.dataset.referRankReady = 'false';
  }

  /**
   * @param {MouseEvent} event
   */
  onClick = (event) => {
    if (event.target.closest('[data-refer-rank-rules-open]') && this.contains(event.target)) {
      this.openRulesDialog();
      return;
    }

    if (event.target.closest('[data-refer-rank-rules-close]') && this.contains(event.target)) {
      this.rulesDialog?.close();
    }
  };

  /**
   * Native dialog does not lock document scroll. Match theme drawers (`html.lock`).
   */
  openRulesDialog() {
    if (!this.rulesDialog || typeof this.rulesDialog.showModal !== 'function') return;
    if (this.rulesDialog.open) return;
    this.rulesDialog.showModal();
    this.setRulesScrollLock(true);
  }

  /**
   * @param {MouseEvent} event
   */
  onRulesDialogClick = (event) => {
    if (event.target === this.rulesDialog) this.rulesDialog.close();
  };

  onRulesDialogClose = () => {
    this.setRulesScrollLock(false);
  };

  /**
   * @param {boolean} isLocked
   */
  setRulesScrollLock(isLocked) {
    const root = document.documentElement;
    root.classList.toggle('is-refer-rank-rules-open', isLocked);
    document.removeEventListener('wheel', this.preventBackgroundScroll);
    document.removeEventListener('touchmove', this.preventBackgroundScroll);

    if (!isLocked) {
      if (typeof this.savedScrollY === 'number') {
        const top = this.savedScrollY;
        this.savedScrollY = undefined;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        window.scrollTo(0, top);
      }
      return;
    }

    this.savedScrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.savedScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.addEventListener('wheel', this.preventBackgroundScroll, { passive: false });
    document.addEventListener('touchmove', this.preventBackgroundScroll, { passive: false });
  }

  /**
   * @param {WheelEvent|TouchEvent} event
   */
  preventBackgroundScroll = (event) => {
    if (!this.rulesDialog?.open) return;
    if (this.rulesDialog.contains(event.target)) return;
    event.preventDefault();
  };

  /**
   * @returns {boolean}
   */
  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * The rows are rendered and masked server-side. The only thing left to do is
   * duplicate them so `translateY(-50%)` loops without a visible seam, and only
   * when there is more to show than fits.
   */
  setupList() {
    const track = this.querySelector('[data-refer-rank-track]');
    const group = this.querySelector('[data-refer-rank-track-group]');
    if (!track || !group) return;

    track.querySelectorAll('[data-refer-rank-track-clone]').forEach((node) => node.remove());
    track.classList.remove('is-scrolling');

    const rowCount = Number(this.dataset.referRankRowCount) || 0;
    const visibleRows = Number(this.dataset.referRankVisibleRows) || 5;
    if (rowCount <= visibleRows || this.prefersReducedMotion()) return;

    const clone = /** @type {HTMLElement} */ (group.cloneNode(true));
    clone.removeAttribute('data-refer-rank-track-group');
    clone.setAttribute('data-refer-rank-track-clone', '');
    clone.setAttribute('aria-hidden', 'true');
    track.appendChild(clone);
    track.classList.add('is-scrolling');
  }

  setupIntersectionPause() {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries.find((candidate) => candidate.target === this);
        this.classList.toggle('is-offscreen', !entry?.isIntersecting);
      },
      { threshold: 0.12 }
    );
    this.intersectionObserver.observe(this);
  }

  /**
   * Prefer `.yotpo-tile-wrapper` so Login / share / Email / Final stay
   * on the same node. Fallback to the visible child view. Never clone.
   */
  setupYotpo() {
    if (!this.yotpoHost || !this.yotpoSlot || this.yotpoMoved) return;

    this.watchYotpoHost();
    const existing = this.findYotpoSurface();
    if (existing) this.moveYotpo(existing);

    this.yotpoTimer = window.setTimeout(() => {
      if (this.yotpoMoved) return;
      if (this.yotpoObserver) {
        this.yotpoObserver.disconnect();
        this.yotpoObserver = null;
      }
      if (this.yotpoFallback) this.yotpoFallback.hidden = false;
    }, ReferRank.YOTPO_TIMEOUT_MS);
  }

  watchYotpoHost() {
    if (this.yotpoObserver || !this.yotpoHost) return;
    this.yotpoObserver = new MutationObserver(() => {
      const node = this.findYotpoSurface();
      if (node) this.moveYotpo(node);
      this.syncYotpoLoginState();
    });
    this.yotpoObserver.observe(this.yotpoHost, { childList: true, subtree: true });
  }

  watchYotpoSlot() {
    if (this.yotpoSlotObserver || !this.yotpoSlot) return;
    this.yotpoSlotObserver = new MutationObserver(() => {
      this.syncYotpoLoginState();
    });
    this.yotpoSlotObserver.observe(this.yotpoSlot, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden'],
    });
  }

  /**
   * Left-panel blur is off for guests. Turn it on only when the logged-in
   * Yotpo share view is the visible surface.
   */
  syncYotpoLoginState() {
    const scope = this.yotpoSlot || this;
    const isLoginVisible = this.isYotpoSurfaceVisible(scope.querySelector('.yotpo-customer-login-view'));
    const isFriendsVisible = this.isYotpoSurfaceVisible(scope.querySelector('.yotpo-friends-emails-view'));
    this.classList.toggle('is-yotpo-logged-in', isFriendsVisible && !isLoginVisible);
  }

  /**
   * @param {Element|null} node
   * @returns {boolean}
   */
  isYotpoSurfaceVisible(node) {
    if (!node) return false;
    const style = window.getComputedStyle(node);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }

  /**
   * @returns {Element|null}
   */
  findYotpoSurface() {
    if (!this.yotpoHost) return null;
    const wrapper = this.yotpoHost.querySelector('.yotpo-tile-wrapper');
    if (wrapper && wrapper.textContent && wrapper.textContent.trim()) return wrapper;
    const candidates = this.yotpoHost.querySelectorAll(
      '.yotpo-friends-emails-view, .yotpo-customer-login-view, .yotpo-final-view, .yotpo-email-view'
    );
    for (const node of candidates) {
      if (node.textContent && node.textContent.trim()) return node;
    }
    return null;
  }

  /**
   * Moves the live Yotpo node into the visible slot. Must not clone.
   * Keeps watching the host because Vue may recreate FinalView there.
   * @param {Element} node
   */
  moveYotpo(node) {
    if (!this.yotpoSlot || node.parentElement === this.yotpoSlot) return;
    this.yotpoSlot.appendChild(node);
    this.yotpoMoved = true;
    this.yotpoSlot.classList.add('is-ready');
    this.yotpoHost?.classList.add('is-hoisted');
    this.watchYotpoSlot();
    this.syncYotpoLoginState();
    if (this.yotpoTimer) {
      window.clearTimeout(this.yotpoTimer);
      this.yotpoTimer = 0;
    }
  }
}

if (!customElements.get('refer-rank')) {
  customElements.define('refer-rank', ReferRank);
}
